<nb-card class="placement-dialog">
  <nb-card-header class="placement-dialog__header">
    <div class="placement-dialog__title">
      <h4>{{ 'pages.tutors.placements.lms.createTitle' | translate }}</h4>
      <p class="placement-dialog__hint">
        {{ course.fullname }} <span class="muted">({{ course.shortname }})</span>
      </p>
    </div>
    <button
      nbButton
      ghost
      status="primary"
      size="small"
      type="button"
      class="placement-dialog__refresh"
      (click)="refreshCourse()"
      [disabled]="saving || refreshingCourse || !course.uuid"
      [nbTooltip]="'pages.tutors.placements.lms.rowActions.refreshCourse' | translate"
      nbTooltipPlacement="top"
    >
      <nb-icon
        [icon]="refreshingCourse ? 'loader-outline' : 'refresh-outline'"
        [class.spin]="refreshingCourse"
      ></nb-icon>
      <span>{{ 'pages.tutors.placements.lms.rowActions.refreshCourse' | translate }}</span>
    </button>
  </nb-card-header>

  <nb-card-body>
    <form [formGroup]="form" class="placement-dialog__form">
      <section class="placement-section placement-section--course">
        <div class="placement-section__header">
          <div>
            <h5>{{ 'pages.tutors.placements.lms.createDialog.courseAgentTitle' | translate }}</h5>
            <p>{{ 'pages.tutors.placements.lms.createDialog.courseAgentHint' | translate }}</p>
          </div>
        </div>

      <div class="form-row">
        <label>{{ 'pages.tutors.placements.lms.createDialog.agentOptional' | translate }}</label>
        <nb-select
          formControlName="bot_id"
          [placeholder]="'pages.tutors.placements.lms.field.tutorPlaceholder' | translate"
          fullWidth
        >
          <nb-select-label>
            <ng-container *ngIf="tutorById(form.value.bot_id) as selectedTutor">
              <span
                class="placement-tutor-label"
                [title]="tutorFullLabel(selectedTutor)"
                [nbTooltip]="tutorFullLabel(selectedTutor)"
                nbTooltipPlacement="top"
              >
                <span class="placement-tutor-label__name">{{ selectedTutor.name }}</span>
                <span
                  *ngIf="tutorVisualName(selectedTutor)"
                  class="placement-tutor-label__visual"
                >
                  {{ tutorVisualName(selectedTutor) }}
                </span>
              </span>
            </ng-container>
          </nb-select-label>
          <nb-option *ngFor="let t of tutors" [value]="t.id">
            <span
              class="placement-tutor-option"
              [title]="tutorFullLabel(t)"
              [nbTooltip]="tutorFullLabel(t)"
              nbTooltipPlacement="top"
            >
              <span class="placement-tutor-option__name">{{ t.name }}</span>
              <span
                *ngIf="tutorVisualName(t)"
                class="placement-tutor-option__visual"
              >
                {{ tutorVisualName(t) }}
              </span>
            </span>
          </nb-option>
        </nb-select>
      </div>
      </section>

      <section class="advanced-positioning" [class.is-open]="showAdvancedPositioning">
        <button
          type="button"
          class="advanced-positioning__toggle"
          (click)="toggleAdvancedPositioning()"
          [attr.aria-expanded]="showAdvancedPositioning"
        >
          <span>{{ 'pages.tutors.placements.lms.advancedPositioning' | translate }}</span>
          <nb-icon icon="chevron-down-outline"></nb-icon>
        </button>

        <div *ngIf="showAdvancedPositioning" class="advanced-positioning__fields">
          <div class="form-row">
            <label>{{ 'pages.tutors.placements.lms.field.pagePattern' | translate }}</label>
            <nb-select formControlName="page_pattern" fullWidth>
              <nb-option *ngFor="let p of pagePatternOptions" [value]="p.value">
                {{ p.label }}
              </nb-option>
            </nb-select>
          </div>

          <div class="form-row">
            <label>{{ 'pages.tutors.placements.lms.field.blockRegion' | translate }}</label>
            <nb-select formControlName="block_region" fullWidth>
              <nb-option *ngFor="let r of regionOptions" [value]="r.value">
                {{ r.label }}
              </nb-option>
            </nb-select>
          </div>

          <div class="form-row">
            <label>{{ 'pages.tutors.placements.lms.field.blockWeight' | translate }}</label>
            <input type="number" nbInput fieldSize="small" formControlName="block_weight" />
          </div>
        </div>
      </section>

      <section class="placement-section placement-section--activities">
        <div class="placement-section__header">
          <div>
            <h5>{{ 'pages.tutors.placements.lms.createDialog.activityPlacementsTitle' | translate }}</h5>
            <p>
              {{ 'pages.tutors.placements.lms.createDialog.activityCount' | translate:{
                selected: selectedActivityCount(),
                total: totalActivityCount()
              } }}
            </p>
          </div>
          <nb-icon
            icon="info-outline"
            [nbTooltip]="'pages.tutors.placements.lms.createDialog.activityTooltip' | translate"
            nbTooltipPlacement="top"
          ></nb-icon>
        </div>

        <div *ngIf="loadingActivities" class="activity-loading">
          <nb-spinner status="basic" size="tiny"></nb-spinner>
          <span>{{ 'pages.tutors.placements.lms.createDialog.activitiesLoading' | translate }}</span>
        </div>

        <div *ngIf="!loadingActivities && activityGroups.length === 0" class="activity-empty">
          {{ 'pages.tutors.placements.lms.createDialog.noActivities' | translate }}
        </div>

        <div
          *ngFor="let group of activityGroups"
          class="activity-group"
          [class.is-disabled]="!group.enabled"
        >
          <div class="activity-group__main">
            <nb-checkbox
              [checked]="group.enabled"
              (checkedChange)="toggleGroup(group, $event)"
              [nbTooltip]="'pages.tutors.placements.lms.createDialog.groupTooltip' | translate:{ label: groupLabel(group) }"
              nbTooltipPlacement="top"
            ></nb-checkbox>

            <button
              type="button"
              class="activity-group__title"
              (click)="toggleGroupExpanded(group)"
            >
              <span>{{ groupLabel(group) }}</span>
              <small>{{ 'pages.tutors.placements.lms.createDialog.activitiesCountShort' | translate:{ count: group.activities.length } }}</small>
              <nb-icon [icon]="group.expanded ? 'chevron-up-outline' : 'chevron-down-outline'"></nb-icon>
            </button>

            <nb-select
              [(ngModel)]="group.botId"
              [ngModelOptions]="{ standalone: true }"
              [disabled]="!group.enabled"
              size="small"
              [nbTooltip]="'pages.tutors.placements.lms.createDialog.groupAgentTooltip' | translate"
              nbTooltipPlacement="top"
            >
              <nb-select-label>
                <ng-container *ngIf="groupHasTutorOverride(group) && tutorById(group.botId) as selectedGroupTutor; else inheritedGroupTutor">
                  <span
                    class="placement-tutor-label"
                    [title]="tutorFullLabel(selectedGroupTutor)"
                    [nbTooltip]="tutorFullLabel(selectedGroupTutor)"
                    nbTooltipPlacement="top"
                  >
                    <span class="placement-tutor-label__name">{{ selectedGroupTutor.name }}</span>
                    <span
                      *ngIf="tutorVisualName(selectedGroupTutor)"
                      class="placement-tutor-label__visual"
                    >
                      {{ tutorVisualName(selectedGroupTutor) }}
                    </span>
                  </span>
                </ng-container>
                <ng-template #inheritedGroupTutor>
                  <span class="placement-agent-inherit-label">
                    <span class="placement-agent-inherit-label__text">
                      {{ 'pages.tutors.placements.lms.createDialog.sameAsCourseAgent' | translate }}
                    </span>
                    <span class="placement-agent-inherit-label__tag">
                      {{ 'pages.tutors.placements.lms.createDialog.sameAsCourseAgentTag' | translate }}
                    </span>
                  </span>
                </ng-template>
              </nb-select-label>
              <nb-option [value]="inheritCourseAgentValue">
                <span class="placement-agent-inherit-option">
                  <span>{{ 'pages.tutors.placements.lms.createDialog.sameAsCourseAgent' | translate }}</span>
                  <span class="placement-agent-inherit-option__tag">
                    {{ 'pages.tutors.placements.lms.createDialog.sameAsCourseAgentTag' | translate }}
                  </span>
                </span>
              </nb-option>
              <nb-option *ngFor="let t of tutors" [value]="t.id">
                <span
                  class="placement-tutor-option"
                  [title]="tutorFullLabel(t)"
                  [nbTooltip]="tutorFullLabel(t)"
                  nbTooltipPlacement="top"
                >
                  <span class="placement-tutor-option__name">{{ t.name }}</span>
                  <span
                    *ngIf="tutorVisualName(t)"
                    class="placement-tutor-option__visual"
                  >
                    {{ tutorVisualName(t) }}
                  </span>
                </span>
              </nb-option>
            </nb-select>
          </div>

          <div *ngIf="group.expanded" class="activity-group__items">
            <div *ngFor="let activity of group.activities" class="activity-item">
              <span class="activity-item__name">{{ activity.name }}</span>
              <span class="activity-item__meta">
                {{ activity.section_name || ('pages.tutors.placements.lms.createDialog.sectionFallback' | translate:{ number: activity.section_number }) }}
                · {{ 'pages.tutors.placements.lms.createDialog.cmid' | translate:{ cmid: activity.cmid } }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div *ngIf="errorDetail" class="placement-dialog__error">
        <nb-icon icon="alert-triangle-outline"></nb-icon>
        <span>{{ errorDetail }}</span>
      </div>
    </form>
  </nb-card-body>

  <nb-card-footer class="placement-dialog__footer">
    <button nbButton ghost status="basic" size="small" (click)="cancel()" [disabled]="saving">
      {{ 'buttons.cancel' | translate }}
    </button>
    <button
      nbButton
      status="primary"
      size="small"
      (click)="save()"
      [disabled]="saving || form.invalid"
    >
      <nb-icon *ngIf="saving" icon="loader-outline" class="spin"></nb-icon>
      {{ 'pages.tutors.placements.lms.placeAction' | translate }}
    </button>
  </nb-card-footer>
</nb-card>
